import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Bold, Italic, Underline, Strikethrough,
    Palette, Heading1, Heading2, Heading3,
    List, ListOrdered, CheckSquare, Quote, Code,
    Minus, Undo, Redo, Check, Type, Paintbrush
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Color options for text colors
const TEXT_COLORS = [
    { name: 'Default', color: null, bg: '#374151', label: 'Default' },
    { name: 'Gray', color: '#6b7280', bg: '#6b7280', label: 'Gray' },
    { name: 'Brown', color: '#92400e', bg: '#92400e', label: 'Brown' },
    { name: 'Orange', color: '#ea580c', bg: '#ea580c', label: 'Orange' },
    { name: 'Yellow', color: '#ca8a04', bg: '#ca8a04', label: 'Yellow' },
    { name: 'Green', color: '#16a34a', bg: '#16a34a', label: 'Green' },
    { name: 'Blue', color: '#2563eb', bg: '#2563eb', label: 'Blue' },
    { name: 'Purple', color: '#9333ea', bg: '#9333ea', label: 'Purple' },
    { name: 'Pink', color: '#db2777', bg: '#db2777', label: 'Pink' },
    { name: 'Red', color: '#dc2626', bg: '#dc2626', label: 'Red' },
];

// Color options for background/highlight colors
const BACKGROUND_COLORS = [
    { name: 'None', color: null, bg: 'transparent', border: '#d1d5db', label: 'None' },
    { name: 'Gray', color: '#f3f4f6', bg: '#f3f4f6', label: 'Gray' },
    { name: 'Brown', color: '#fef3c7', bg: '#fef3c7', label: 'Brown' },
    { name: 'Orange', color: '#ffedd5', bg: '#ffedd5', label: 'Orange' },
    { name: 'Yellow', color: '#fef9c3', bg: '#fef9c3', label: 'Yellow' },
    { name: 'Green', color: '#dcfce7', bg: '#dcfce7', label: 'Green' },
    { name: 'Blue', color: '#dbeafe', bg: '#dbeafe', label: 'Blue' },
    { name: 'Purple', color: '#f3e8ff', bg: '#f3e8ff', label: 'Purple' },
    { name: 'Pink', color: '#fce7f3', bg: '#fce7f3', label: 'Pink' },
    { name: 'Red', color: '#fee2e2', bg: '#fee2e2', label: 'Red' },
];

const EditorToolbar = ({ editor }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [activeTab, setActiveTab] = useState('text'); // 'text' or 'background'
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const colorPickerRef = useRef(null);
    const colorButtonRef = useRef(null);

    // Get current text color and highlight
    const currentColor = editor.getAttributes('textStyle').color || null;
    const currentHighlight = editor.getAttributes('highlight').color || null;

    // Calculate picker position based on button location
    const updatePickerPosition = useCallback(() => {
        if (colorButtonRef.current) {
            const buttonRect = colorButtonRef.current.getBoundingClientRect();
            const pickerWidth = 240; // minWidth of picker
            const pickerHeight = 280; // approximate height

            // Calculate position, ensuring it stays within viewport
            let left = buttonRect.left + buttonRect.width / 2 - pickerWidth / 2;
            let top = buttonRect.bottom + 8; // 8px below button

            // Adjust if picker would go off right edge
            if (left + pickerWidth > window.innerWidth - 16) {
                left = window.innerWidth - pickerWidth - 16;
            }
            // Adjust if picker would go off left edge
            if (left < 16) {
                left = 16;
            }
            // Adjust if picker would go off bottom edge
            if (top + pickerHeight > window.innerHeight - 16) {
                top = buttonRect.top - pickerHeight - 8; // Show above button
            }

            setPickerPosition({ top, left });
        }
    }, []);

    // Update position when picker opens
    useEffect(() => {
        if (showColorPicker) {
            updatePickerPosition();
        }
    }, [showColorPicker, updatePickerPosition]);

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showColorPicker &&
                colorPickerRef.current &&
                !colorPickerRef.current.contains(event.target) &&
                colorButtonRef.current &&
                !colorButtonRef.current.contains(event.target)
            ) {
                setShowColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColorPicker]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape' && showColorPicker) {
                setShowColorPicker(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showColorPicker]);

    if (!editor) {
        return null;
    }

    const setTextColor = (color) => {
        if (color === null) {
            editor.chain().focus().unsetColor().run();
        } else {
            editor.chain().focus().setColor(color).run();
        }
        setShowColorPicker(false);
    };

    const setBackgroundHighlight = (color) => {
        if (color === null) {
            editor.chain().focus().unsetHighlight().run();
        } else {
            editor.chain().focus().toggleHighlight({ color }).run();
        }
        setShowColorPicker(false);
    };

    const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`
                p-2 rounded-lg transition-none
                ${isActive
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-blue-500 hover:text-white'
                }
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-600
            `}
        >
            {children}
        </button>
    );

    const Divider = () => (
        <div className="w-px h-5 bg-slate-200 mx-1" />
    );

    return (
        <div className="sticky top-4 z-10 px-4 py-2">
            <div className="flex items-center justify-center gap-0.5 flex-wrap max-w-4xl mx-auto">
                {/* Solid Opaque Toolbar Container */}
                <div className="inline-flex items-center gap-0.5 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-md">

                    {/* Text Style */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        title="Bold (Ctrl+B)"
                    >
                        <Bold size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        title="Italic (Ctrl+I)"
                    >
                        <Italic size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        title="Underline (Ctrl+U)"
                    >
                        <Underline size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        title="Strikethrough"
                    >
                        <Strikethrough size={15} />
                    </ToolbarButton>

                    <Divider />

                    {/* Headings */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        isActive={editor.isActive('heading', { level: 1 })}
                        title="Heading 1"
                    >
                        <Heading1 size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        isActive={editor.isActive('heading', { level: 2 })}
                        title="Heading 2"
                    >
                        <Heading2 size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        isActive={editor.isActive('heading', { level: 3 })}
                        title="Heading 3"
                    >
                        <Heading3 size={15} />
                    </ToolbarButton>

                    <Divider />

                    {/* Lists */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        title="Bullet List"
                    >
                        <List size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        title="Numbered List"
                    >
                        <ListOrdered size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleTaskList().run()}
                        isActive={editor.isActive('taskList')}
                        title="Task List"
                    >
                        <CheckSquare size={15} />
                    </ToolbarButton>

                    <Divider />

                    {/* Block Elements */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        isActive={editor.isActive('blockquote')}
                        title="Quote"
                    >
                        <Quote size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        isActive={editor.isActive('codeBlock')}
                        title="Code Block"
                    >
                        <Code size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        title="Horizontal Rule"
                    >
                        <Minus size={15} />
                    </ToolbarButton>

                    <Divider />

                    {/* Text Color */}
                    <div className="relative">
                        <div ref={colorButtonRef}>
                            <ToolbarButton
                                onClick={() => setShowColorPicker(!showColorPicker)}
                                isActive={editor.isActive('textStyle') || editor.isActive('highlight')}
                                title="Text & Highlight Color"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Palette size={15} />
                                    <div
                                        className="w-3 h-3 rounded-sm border border-slate-300"
                                        style={{
                                            backgroundColor: currentHighlight || currentColor || '#f1f5f9'
                                        }}
                                    />
                                </div>
                            </ToolbarButton>
                        </div>
                    </div>

                    {/* Color Picker Portal */}
                    {showColorPicker && createPortal(
                        <AnimatePresence>
                            <motion.div
                                ref={colorPickerRef}
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                                className="fixed p-4 bg-white rounded-2xl shadow-2xl border border-slate-200"
                                style={{
                                    zIndex: 9999,
                                    top: pickerPosition.top,
                                    left: pickerPosition.left,
                                    minWidth: '240px'
                                }}
                            >
                                {/* Tab Switcher */}
                                <div className="flex mb-4 bg-slate-100 rounded-xl p-1">
                                    <button
                                        onClick={() => setActiveTab('text')}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === 'text'
                                            ? 'bg-white text-slate-800 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                    >
                                        <Type size={14} />
                                        Text
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('background')}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === 'background'
                                            ? 'bg-white text-slate-800 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                    >
                                        <Paintbrush size={14} />
                                        Background
                                    </button>
                                </div>

                                {/* Color Grid */}
                                <div className="space-y-3">
                                    {activeTab === 'text' ? (
                                        <>
                                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">Text Color</div>
                                            <div className="grid grid-cols-5 gap-2">
                                                {TEXT_COLORS.map((colorOption) => {
                                                    const isSelected = currentColor === colorOption.color;
                                                    return (
                                                        <motion.button
                                                            key={colorOption.name}
                                                            whileHover={{ scale: 1.15 }}
                                                            whileTap={{ scale: 0.9 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setTextColor(colorOption.color);
                                                            }}
                                                            className={`
                                                                w-9 h-9 rounded-xl flex items-center justify-center
                                                                transition-all duration-150 ease-out cursor-pointer
                                                                ${isSelected
                                                                    ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white'
                                                                    : 'hover:ring-2 hover:ring-slate-300 hover:ring-offset-1 hover:ring-offset-white'
                                                                }
                                                            `}
                                                            style={{
                                                                backgroundColor: colorOption.bg,
                                                                border: colorOption.color === null ? '2px solid #e2e8f0' : 'none'
                                                            }}
                                                            title={colorOption.label}
                                                        >
                                                            {isSelected && (
                                                                <Check
                                                                    size={16}
                                                                    className={colorOption.color === null ? 'text-slate-600' : 'text-white'}
                                                                />
                                                            )}
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">Background Color</div>
                                            <div className="grid grid-cols-5 gap-2">
                                                {BACKGROUND_COLORS.map((colorOption) => {
                                                    const isSelected = currentHighlight === colorOption.color;
                                                    return (
                                                        <motion.button
                                                            key={colorOption.name}
                                                            whileHover={{ scale: 1.15 }}
                                                            whileTap={{ scale: 0.9 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setBackgroundHighlight(colorOption.color);
                                                            }}
                                                            className={`
                                                                w-9 h-9 rounded-xl flex items-center justify-center
                                                                transition-all duration-150 ease-out cursor-pointer
                                                                ${isSelected
                                                                    ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white'
                                                                    : 'hover:ring-2 hover:ring-slate-300 hover:ring-offset-1 hover:ring-offset-white'
                                                                }
                                                            `}
                                                            style={{
                                                                backgroundColor: colorOption.bg,
                                                                border: colorOption.border ? `2px solid ${colorOption.border}` : '2px solid transparent'
                                                            }}
                                                            title={colorOption.label}
                                                        >
                                                            {isSelected && (
                                                                <Check
                                                                    size={16}
                                                                    className="text-slate-700"
                                                                />
                                                            )}
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Quick Actions */}
                                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTextColor(null);
                                            setBackgroundHighlight(null);
                                        }}
                                        className="flex-1 py-2 px-3 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                    >
                                        Reset All
                                    </button>
                                </div>
                            </motion.div>
                        </AnimatePresence>,
                        document.body
                    )}

                    <Divider />

                    {/* Undo/Redo */}
                    <ToolbarButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        title="Undo (Ctrl+Z)"
                    >
                        <Undo size={15} />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        title="Redo (Ctrl+Y)"
                    >
                        <Redo size={15} />
                    </ToolbarButton>
                </div>
            </div>
        </div>
    );
};

export default EditorToolbar;
